// features/products/pages/ProductDetailPage.tsx (REDESIGNED)
/**
 * Product Detail Page - Simplified & Powerful
 * 
 * Clean overview with quick action cards linking to standalone pages.
 * Tabs: Overview, Pricing, Specifications, Activity
 * Stock summary card at top
 * Quick links to: Variants, UOMs, Images, Stock, Batches, Barcodes
 */

import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import {
    getProduct, deleteProduct, restoreProduct, toggleClearance,
    duplicateProduct, getProductStockLevels, getProductPriceHistory,
    ProductData, ProductStockLevel, PriceHistoryEntry,
} from '../services/productsApi';
import {
    ArrowLeft, Pencil, Trash2, Copy, Tag, Package, Barcode, Calendar,
    Pill, Ruler, DollarSign, Clock, Layers, Boxes,
    ShieldAlert, RotateCcw, CheckCircle, X, Sparkles,
    Image, ClipboardCheck, TrendingUp, TrendingDown,
    Eye, Activity, Box, Store, ExternalLink, ChevronRight,
    AlertTriangle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

type DetailTab = 'overview' | 'pricing' | 'specifications' | 'activity';

export function ProductDetailPage() {
    const { productId } = useParams<{ productId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [activeTab, setActiveTab] = useState<DetailTab>('overview');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [duplicateName, setDuplicateName] = useState('');

    const { data: product, isLoading, isError, refetch } = useQuery<ProductData>({
        queryKey: ['product', productId],
        queryFn: () => getProduct(user!.tenantId, productId!),
        enabled: !!user?.tenantId && !!productId,
    });

    const { data: stockLevels } = useQuery<ProductStockLevel[]>({
        queryKey: ['product-stock-levels', productId],
        queryFn: () => getProductStockLevels(user!.tenantId, productId!),
        enabled: !!user?.tenantId && !!productId,
    });

    const { data: priceHistory = [], isLoading: isLoadingHistory } = useQuery<PriceHistoryEntry[]>({
        queryKey: ['product-price-history', productId],
        queryFn: () => getProductPriceHistory(user!.tenantId, productId!),
        enabled: !!user?.tenantId && !!productId,
    });

    // Mutations
    const toggleClearanceMutation = useMutation({
        mutationFn: () => toggleClearance(user!.tenantId, productId!),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['product', productId] }); addToast({ type: 'success', title: 'Clearance toggled' }); },
    });

    const deleteMutation = useMutation({
        mutationFn: () => deleteProduct(user!.tenantId, productId!),
        onSuccess: () => { addToast({ type: 'success', title: 'Product deleted' }); navigate('/products'); },
    });

    const restoreMutation = useMutation({
        mutationFn: () => restoreProduct(user!.tenantId, productId!),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['product', productId] }); addToast({ type: 'success', title: 'Product restored' }); },
    });

    const duplicateMutation = useMutation({
        mutationFn: (name?: string) => duplicateProduct(user!.tenantId, productId!, name ? { name } : {}),
        onSuccess: (data) => {
            setShowDuplicateModal(false);
            addToast({ type: 'success', title: 'Product duplicated', message: `Created "${data.name}"` });
            navigate(`/products/${data.id}`);
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Duplicate failed', message: err?.message }),
    });

    const formatNaira = (val: string) => `₦${parseFloat(val || '0').toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const formatDate = (dateStr: string) => dateStr ? new Date(dateStr).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
    const formatDateTime = (dateStr: string) => dateStr ? new Date(dateStr).toLocaleString('en-NG', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

    if (isLoading) {
        return <div className="min-h-screen flex items-center justify-center"><Spinner size="xl" /></div>;
    }
    if (isError || !product) {
        return <ErrorState title="Product not found" onRetry={() => navigate('/products')} />;
    }

    const profitAmount = parseFloat(product.retailPrice || '0') - parseFloat(product.costPrice || '0');
    const profitMargin = parseFloat(product.retailPrice) > 0 ? (profitAmount / parseFloat(product.retailPrice)) * 100 : 0;
    const isProfitable = profitAmount > 0;

    // Stock totals
    const totalOnHand = stockLevels?.reduce((sum, l) => sum + parseFloat(l.quantityOnHand || '0'), 0) || 0;
    const totalAvailable = stockLevels?.reduce((sum, l) => sum + parseFloat(l.availableQuantity || '0'), 0) || 0;
    const branchCount = stockLevels?.length || 0;

    const tabs = [
        { id: 'overview' as DetailTab, label: 'Overview', icon: Eye },
        { id: 'pricing' as DetailTab, label: 'Pricing', icon: DollarSign },
        { id: 'specifications' as DetailTab, label: 'Specifications', icon: ClipboardCheck },
        { id: 'activity' as DetailTab, label: 'Activity', icon: Activity },
    ];

    const quickLinks = [
        { label: 'Variants', count: product.variants?.length || 0, icon: Boxes, path: `/products/${productId}/variants`, color: 'from-purple-500 to-indigo-600' },
        { label: 'Units (UOMs)', count: product.uoms?.length || 0, icon: Ruler, path: `/products/${productId}/uoms`, color: 'from-cyan-500 to-blue-600' },
        { label: 'Images', count: product.imageUrls?.length || 0, icon: Image, path: `/products/${productId}/images`, color: 'from-pink-500 to-rose-600' },
        { label: 'Stock', count: branchCount, icon: Layers, path: `/products/${productId}/stock`, color: 'from-blue-500 to-indigo-600' },
        { label: 'Batches', count: product.batches?.length || 0, icon: Package, path: `/products/${productId}/batches`, color: 'from-amber-500 to-orange-600' },
        { label: 'Barcodes', count: product.barcodes?.length || 0, icon: Barcode, path: `/products/${productId}/barcodes`, color: 'from-slate-500 to-slate-700' },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* ========== HEADER ========== */}
            <div className="relative bg-white/95 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-30 shadow-sm">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-50/40 via-transparent to-transparent pointer-events-none" />
                <div className="relative max-w-[1600px] mx-auto px-6 lg:px-8 py-5">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-4 min-w-0">
                            <button onClick={() => navigate('/products')} className="p-2.5 rounded-xl hover:bg-slate-100 flex-shrink-0" type="button"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
                            <div className="relative flex-shrink-0">
                                <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-xl shadow-emerald-500/30"><Package className="w-6 h-6 text-white" /></div>
                                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-sm">
                                    <div className={clsx('w-2.5 h-2.5 rounded-full', product.isActive ? 'bg-emerald-500' : 'bg-rose-500')} />
                                </div>
                            </div>
                            <div className="min-w-0">
                                <h1 className="text-2xl font-bold text-slate-900 truncate">{product.name}</h1>
                                <div className="flex items-center gap-3 mt-1 flex-wrap">
                                    {product.brand && <span className="text-sm font-semibold text-slate-600">{product.brand}</span>}
                                    {product.categoryName && <span className="text-sm text-slate-400">{product.categoryName}</span>}
                                    {product.sku && <code className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md font-mono">{product.sku}</code>}
                                    {product.deletedAt && <Badge variant="danger" size="sm">Deleted</Badge>}
                                    {product.isClearance && !product.deletedAt && <Badge variant="warning" size="sm">Clearance</Badge>}
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                            {product.deletedAt ? (
                                <button onClick={() => restoreMutation.mutate()} disabled={restoreMutation.isPending}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl text-sm font-semibold shadow-lg" type="button">
                                    <RotateCcw className="w-4 h-4" /> Restore
                                </button>
                            ) : (
                                <>
                                    {product.clearancePrice && (
                                        <button onClick={() => toggleClearanceMutation.mutate()}
                                            className={clsx('flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold shadow-sm',
                                                product.isClearance ? 'bg-amber-50 border border-amber-200 text-amber-700' : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white')} type="button">
                                            <Tag className="w-4 h-4" /> {product.isClearance ? 'End Clearance' : 'Clearance'}
                                        </button>
                                    )}
                                    <button onClick={() => setShowDuplicateModal(true)}
                                        className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50" type="button">
                                        <Copy className="w-4 h-4" /> Duplicate
                                    </button>
                                    <button onClick={() => navigate(`/products/${productId}/edit`)}
                                        className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50" type="button">
                                        <Pencil className="w-4 h-4" /> Edit
                                    </button>
                                    <button onClick={() => setShowDeleteConfirm(true)}
                                        className="flex items-center gap-2 px-4 py-2.5 border border-rose-200 text-rose-600 rounded-xl text-sm font-semibold hover:bg-rose-50" type="button">
                                        <Trash2 className="w-4 h-4" /> Delete
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* ========== STOCK SUMMARY CARD ========== */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <QuickStat label="Total On Hand" value={totalOnHand.toLocaleString()} icon={Box} gradient="from-emerald-500 to-teal-600" />
                    <QuickStat label="Available" value={totalAvailable.toLocaleString()} icon={CheckCircle} gradient="from-blue-500 to-indigo-600" />
                    <QuickStat label="Branches" value={branchCount} icon={Store} gradient="from-purple-500 to-indigo-600" />
                    <QuickStat label="Profit/Unit" value={`${isProfitable ? '+' : ''}₦${profitAmount.toLocaleString()}`} icon={isProfitable ? TrendingUp : TrendingDown}
                        gradient={isProfitable ? 'from-emerald-500 to-teal-600' : 'from-rose-500 to-rose-600'} />
                </div>

                {/* ========== QUICK LINK CARDS ========== */}
                <div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Manage</p>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                        {quickLinks.map(link => (
                            <motion.button key={link.label} whileHover={{ y: -3 }} whileTap={{ scale: 0.98 }}
                                onClick={() => navigate(link.path)}
                                className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 text-center hover:shadow-lg transition-all" type="button">
                                <div className={clsx('p-2.5 rounded-xl bg-gradient-to-br shadow-lg w-fit mx-auto mb-2', link.color)}>
                                    <link.icon className="w-5 h-5 text-white" />
                                </div>
                                <p className="text-xs font-semibold text-slate-700">{link.label}</p>
                                <p className="text-lg font-bold text-slate-900">{link.count}</p>
                            </motion.button>
                        ))}
                    </div>
                </div>

                {/* ========== TABS & CONTENT ========== */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    {/* Tabs */}
                    <div className="flex items-center gap-1 p-2 bg-gradient-to-r from-slate-50/80 to-white border-b border-slate-100">
                        {tabs.map(tab => (
                            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                                className={clsx('flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all',
                                    activeTab === tab.id ? 'bg-white text-emerald-700 shadow-sm shadow-slate-200/50' : 'text-slate-500 hover:text-slate-700')} type="button">
                                <tab.icon className="w-4 h-4" />{tab.label}
                            </button>
                        ))}
                    </div>

                    <AnimatePresence mode="wait">
                        {/* ================================================================ */}
                        {/* OVERVIEW TAB */}
                        {/* ================================================================ */}
                        {activeTab === 'overview' && (
                            <motion.div key="overview" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                                {/* Description */}
                                {(product.description || product.shortDescription) && (
                                    <div className="p-5 border-b border-slate-100">
                                        {product.shortDescription && <p className="text-sm font-bold text-slate-800 mb-2">{product.shortDescription}</p>}
                                        {product.description && <p className="text-sm text-slate-500 leading-relaxed">{product.description}</p>}
                                    </div>
                                )}

                                {/* Key Details Grid */}
                                <div className="p-5">
                                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Product Details</p>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        <DetailItem label="Brand" value={product.brand} />
                                        <DetailItem label="Manufacturer" value={product.manufacturer} />
                                        <DetailItem label="Category" value={product.categoryName} />
                                        <DetailItem label="Supplier" value={product.supplierName} />
                                        <DetailItem label="SKU" value={product.sku} mono />
                                        <DetailItem label="Barcode" value={product.barcode} mono />
                                        <DetailItem label="MPN" value={product.mpn} mono />
                                        <DetailItem label="Slug" value={product.slug} mono />
                                        <DetailItem label="Product Type" value={product.productType?.replace('_', ' ')} />
                                        <DetailItem label="Stock Management" value={product.stockManagement} />
                                        <DetailItem label="Track Inventory" value={product.trackInventory ? 'Yes' : 'No'} />
                                        <DetailItem label="Status">
                                            <Badge variant={product.isActive ? 'success' : 'danger'} size="sm">{product.isActive ? 'Active' : 'Inactive'}</Badge>
                                        </DetailItem>
                                    </div>
                                </div>

                                {/* Tags */}
                                {product.tags && product.tags.length > 0 && (
                                    <div className="px-5 pb-5">
                                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Tags</p>
                                        <div className="flex flex-wrap gap-2">
                                            {product.tags.map((tag, i) => (
                                                <span key={i} className="px-3 py-1.5 bg-slate-100 border border-slate-200 text-slate-600 rounded-lg text-xs font-semibold">{tag}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Features */}
                                <div className="px-5 pb-5">
                                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Features</p>
                                    <div className="flex flex-wrap gap-2">
                                        {product.isFeatured && <Badge variant="info" size="sm">Featured</Badge>}
                                        {product.hasVariants && <Badge variant="info" size="sm">{product.variants?.length} Variants</Badge>}
                                        {product.hasExpiry && <Badge variant="warning" size="sm">Expiry Tracked</Badge>}
                                        {product.trackInventory && <Badge variant="success" size="sm">Inventory Tracked</Badge>}
                                        {product.isAgeRestricted && <Badge variant="danger" size="sm">Age Restricted</Badge>}
                                        {product.isReturnable && <Badge variant="info" size="sm">Returnable</Badge>}
                                        {product.isPharmaceutical && <Badge variant="info" size="sm">Pharmaceutical</Badge>}
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* ================================================================ */}
                        {/* PRICING TAB */}
                        {/* ================================================================ */}
                        {activeTab === 'pricing' && (
                            <motion.div key="pricing" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                                <div className="p-5">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                        <div className="text-center p-4 bg-slate-50 rounded-xl">
                                            <p className="text-[10px] text-slate-400 uppercase">Cost Price</p>
                                            <p className="text-xl font-bold text-slate-700">{formatNaira(product.costPrice)}</p>
                                        </div>
                                        <div className="text-center p-4 bg-emerald-50 rounded-xl">
                                            <p className="text-[10px] text-emerald-600 uppercase">Retail Price</p>
                                            <p className="text-xl font-bold text-emerald-700">{formatNaira(product.retailPrice)}</p>
                                        </div>
                                        <div className="text-center p-4 bg-slate-50 rounded-xl">
                                            <p className="text-[10px] text-slate-400 uppercase">Wholesale</p>
                                            <p className="text-xl font-bold text-slate-700">{product.wholesalePrice ? formatNaira(product.wholesalePrice) : '—'}</p>
                                        </div>
                                        <div className="text-center p-4 bg-amber-50 rounded-xl">
                                            <p className="text-[10px] text-amber-600 uppercase">Clearance</p>
                                            <p className="text-xl font-bold text-amber-700">{product.clearancePrice ? formatNaira(product.clearancePrice) : '—'}</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        <DetailItem label="Minimum Price" value={product.minimumPrice ? formatNaira(product.minimumPrice) : '—'} />
                                        <DetailItem label="Max Discount" value={product.maximumDiscount ? `${product.maximumDiscount}%` : '—'} />
                                        <DetailItem label="Tax Rate" value={`${product.taxRate}%`} />
                                        <DetailItem label="Tax Category" value={product.taxCategory || 'standard'} />
                                        <DetailItem label="Tax Inclusive" value={product.taxInclusive ? 'Yes' : 'No'} />
                                    </div>
                                    {/* Profit Analysis */}
                                    <div className="mt-6 p-5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl text-white">
                                        <p className="text-sm font-bold uppercase tracking-wider mb-4">Profit Analysis</p>
                                        <div className="grid grid-cols-3 gap-4 text-center">
                                            <div className="p-3 bg-white/10 rounded-xl"><p className="text-[10px] text-emerald-100 uppercase">Profit/Unit</p><p className="text-xl font-black">{isProfitable ? '+' : ''}{formatNaira(String(profitAmount))}</p></div>
                                            <div className="p-3 bg-white/10 rounded-xl"><p className="text-[10px] text-emerald-100 uppercase">Margin</p><p className="text-xl font-black">{profitMargin.toFixed(1)}%</p></div>
                                            <div className="p-3 bg-white/10 rounded-xl"><p className="text-[10px] text-emerald-100 uppercase">Markup</p><p className="text-xl font-black">{parseFloat(product.costPrice) > 0 ? ((profitAmount / parseFloat(product.costPrice)) * 100).toFixed(1) : '0'}%</p></div>
                                        </div>
                                    </div>

                                    {/* Price History Timeline */}
                                    <div className="mt-8 pt-6 border-t border-slate-100">
                                        <div className="flex items-center justify-between mb-4">
                                            <div>
                                                <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                                                    <Clock className="w-4 h-4 text-emerald-600" />
                                                    <span>Price Adjustment History</span>
                                                </h4>
                                                <p className="text-xs text-slate-500">
                                                    Audited timeline of cost and retail price updates from GRN receipts and catalog edits
                                                </p>
                                            </div>
                                            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                                                {priceHistory.length} adjustments
                                            </span>
                                        </div>

                                        {isLoadingHistory ? (
                                            <div className="py-6 flex items-center justify-center gap-2 text-xs text-slate-400">
                                                <Spinner size="sm" />
                                                <span>Loading price history...</span>
                                            </div>
                                        ) : priceHistory.length === 0 ? (
                                            <div className="p-6 bg-slate-50 rounded-xl text-center text-xs text-slate-500">
                                                No historical price adjustments recorded yet. Current catalog price remains as initialized.
                                            </div>
                                        ) : (
                                            <div className="space-y-2.5">
                                                {priceHistory.map((entry) => {
                                                    const oldVal = entry.oldPrice ? parseFloat(entry.oldPrice) : null;
                                                    const newVal = parseFloat(entry.newPrice);
                                                    const isIncrease = oldVal !== null ? newVal > oldVal : true;
                                                    const diff = oldVal !== null ? Math.abs(newVal - oldVal) : null;

                                                    return (
                                                        <div
                                                            key={entry.id}
                                                            className="flex flex-col sm:flex-row sm:items-center justify-between p-3.5 bg-slate-50/80 hover:bg-slate-50 rounded-xl border border-slate-100 transition-colors text-xs gap-2"
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div className={clsx(
                                                                    "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                                                                    isIncrease ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                                                                )}>
                                                                    {isIncrease ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                                                                </div>
                                                                <div>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-bold uppercase tracking-wider text-[10px] px-1.5 py-0.5 rounded bg-slate-200/60 text-slate-700">
                                                                            {entry.priceType} Price
                                                                        </span>
                                                                        <span className="font-semibold text-slate-800">
                                                                            {oldVal !== null ? formatNaira(String(oldVal)) : 'Initial'}
                                                                        </span>
                                                                        <span className="text-slate-400">→</span>
                                                                        <span className="font-bold text-slate-900">
                                                                            {formatNaira(String(newVal))}
                                                                        </span>
                                                                        {diff !== null && (
                                                                            <span className={clsx(
                                                                                "text-[10px] font-bold px-1.5 py-0.2 rounded",
                                                                                isIncrease ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
                                                                            )}>
                                                                                {isIncrease ? '+' : '-'}{formatNaira(String(diff))}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <p className="text-[11px] text-slate-500 mt-0.5">
                                                                        {entry.reason}
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            <div className="text-right sm:text-right text-[11px] text-slate-400 flex sm:flex-col items-center sm:items-end justify-between">
                                                                <span className="font-medium text-slate-600">{entry.changedByName}</span>
                                                                <span>{formatDateTime(entry.createdAt)}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* ================================================================ */}
                        {/* SPECIFICATIONS TAB */}
                        {/* ================================================================ */}
                        {activeTab === 'specifications' && (
                            <motion.div key="specifications" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                                <div className="p-5 space-y-6">
                                    {/* Stock Settings */}
                                    <div>
                                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Stock & Inventory</p>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            <DetailItem label="Stock Management" value={product.stockManagement} />
                                            <DetailItem label="Track Inventory" value={product.trackInventory ? 'Yes' : 'No'} />
                                            <DetailItem label="Low Stock Alert" value={String(product.lowStockThreshold || 10)} />
                                            <DetailItem label="Reorder Point" value={product.reorderPoint ? String(product.reorderPoint) : '—'} />
                                        </div>
                                    </div>

                                    {/* Pharmacy */}
                                    {product.isPharmaceutical && (
                                        <div>
                                            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Pharmacy</p>
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                                <DetailItem label="Classification" value={product.drugClassification} />
                                                <DetailItem label="Prescription" value={product.requiresPrescription ? 'Required' : 'Not Required'} />
                                                <DetailItem label="Active Ingredients" value={product.activeIngredients} />
                                                <DetailItem label="Storage" value={product.storageRequirements} />
                                            </div>
                                        </div>
                                    )}

                                    {/* Expiry */}
                                    <div>
                                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Expiry</p>
                                        <div className="grid grid-cols-2 gap-3">
                                            <DetailItem label="Has Expiry" value={product.hasExpiry ? 'Yes' : 'No'} />
                                            {product.hasExpiry && <DetailItem label="Shelf Life" value={product.shelfLifeDays ? `${product.shelfLifeDays} days` : '—'} />}
                                        </div>
                                    </div>

                                    {/* Restrictions */}
                                    <div>
                                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Restrictions</p>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                            <DetailItem label="Age Restricted" value={product.isAgeRestricted ? 'Yes' : 'No'} />
                                            {product.isAgeRestricted && <DetailItem label="Minimum Age" value={String(product.minimumAge || '—')} />}
                                            <DetailItem label="ID Required" value={product.requiresID ? 'Yes' : 'No'} />
                                        </div>
                                    </div>

                                    {/* Dimensions */}
                                    <div>
                                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Dimensions & Weight</p>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            <DetailItem label="Weight" value={product.weight ? `${product.weight} ${product.weightUnit}` : '—'} />
                                            <DetailItem label="Length" value={product.length ? `${product.length} ${product.dimensionUnit}` : '—'} />
                                            <DetailItem label="Width" value={product.width ? `${product.width} ${product.dimensionUnit}` : '—'} />
                                            <DetailItem label="Height" value={product.height ? `${product.height} ${product.dimensionUnit}` : '—'} />
                                        </div>
                                    </div>

                                    {/* Returns */}
                                    <div>
                                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Returns</p>
                                        <DetailItem label="Returnable" value={product.isReturnable ? `Yes (${product.returnPeriodDays} days)` : 'No'} />
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* ================================================================ */}
                        {/* ACTIVITY TAB */}
                        {/* ================================================================ */}
                        {activeTab === 'activity' && (
                            <motion.div key="activity" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                                <div className="p-5">
                                    <div className="space-y-6">
                                        {/* Created */}
                                        <div className="flex gap-4">
                                            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0"><Package className="w-5 h-5 text-emerald-600" /></div>
                                            <div>
                                                <p className="text-sm font-bold text-slate-800">Product Created</p>
                                                <p className="text-xs text-slate-500 mt-0.5">{formatDateTime(product.createdAt)}</p>
                                            </div>
                                        </div>

                                        {/* Price Adjustment Events */}
                                        {priceHistory.map((entry) => (
                                            <div key={`act-${entry.id}`} className="flex gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                                                    <DollarSign className="w-5 h-5 text-teal-600" />
                                                </div>
                                                <div className="space-y-0.5">
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-sm font-bold text-slate-800">
                                                            {entry.priceType.toUpperCase()} Price Adjusted: {formatNaira(entry.newPrice)}
                                                        </p>
                                                        {entry.oldPrice && (
                                                            <span className="text-xs text-slate-400">
                                                                (from {formatNaira(entry.oldPrice)})
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-slate-600">{entry.reason}</p>
                                                    <p className="text-[10px] text-slate-400">
                                                        By {entry.changedByName} · {formatDateTime(entry.createdAt)}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}

                                        {/* Updated */}
                                        {product.updatedAt !== product.createdAt && (
                                            <div className="flex gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0"><Pencil className="w-5 h-5 text-blue-600" /></div>
                                                <div>
                                                    <p className="text-sm font-bold text-slate-800">Last Updated</p>
                                                    <p className="text-xs text-slate-500 mt-0.5">{formatDateTime(product.updatedAt)}</p>
                                                </div>
                                            </div>
                                        )}
                                        {/* Clearance */}
                                        {product.isClearance && (
                                            <div className="flex gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0"><Tag className="w-5 h-5 text-amber-600" /></div>
                                                <div><p className="text-sm font-bold text-slate-800">Clearance Active</p><p className="text-xs text-slate-500 mt-0.5">Selling at {formatNaira(product.clearancePrice || '0')}</p></div>
                                            </div>
                                        )}
                                        {/* System Info */}
                                        <div className="pt-4 border-t border-slate-100">
                                            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">System</p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <DetailItem label="Product ID" value={product.id} mono />
                                                <DetailItem label="Created" value={formatDateTime(product.createdAt)} />
                                                <DetailItem label="Updated" value={formatDateTime(product.updatedAt)} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* ========== DELETE CONFIRMATION ========== */}
            <AnimatePresence>
                {showDeleteConfirm && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 text-center">
                            <Trash2 className="w-12 h-12 text-rose-500 mx-auto mb-3" />
                            <h3 className="text-lg font-bold">Delete "{product.name}"?</h3>
                            <p className="text-sm text-slate-500 mt-2">Product will be soft-deleted and can be restored later.</p>
                            <div className="flex gap-3 mt-6">
                                <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 px-4 py-2.5 border rounded-xl text-sm font-medium">Cancel</button>
                                <button onClick={() => deleteMutation.mutate()} className="flex-1 px-4 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-semibold">Delete</button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ========== DUPLICATE MODAL ========== */}
            <AnimatePresence>
                {showDuplicateModal && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6">
                            <h3 className="text-lg font-bold mb-4">Duplicate Product</h3>
                            <p className="text-sm text-slate-500 mb-4">A copy of "{product.name}" will be created with all variants, UOMs, and barcodes. Stock will be reset to 0.</p>
                            <div className="mb-4">
                                <label className="block text-xs font-semibold text-slate-600 mb-1">New Name (optional)</label>
                                <input type="text" value={duplicateName} onChange={e => setDuplicateName(e.target.value)}
                                    className="w-full px-4 py-3 border rounded-xl text-sm" placeholder={`${product.name} (Copy)`} autoFocus />
                            </div>
                            <div className="flex gap-3">
                                <button onClick={() => { setShowDuplicateModal(false); setDuplicateName(''); }} className="flex-1 px-4 py-2.5 border rounded-xl text-sm font-medium">Cancel</button>
                                <button onClick={() => duplicateMutation.mutate(duplicateName || undefined)}
                                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl text-sm font-semibold">Duplicate</button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

// ============================================================
// HELPER COMPONENTS
// ============================================================

function QuickStat({ label, value, icon: Icon, gradient }: { label: string; value: string | number; icon: React.ElementType; gradient: string }) {
    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-lg transition-all">
            <div className="flex items-start justify-between">
                <div><p className="text-xs font-semibold text-slate-500 uppercase">{label}</p><p className="text-2xl font-bold text-slate-900 mt-1">{value}</p></div>
                <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient} shadow-lg`}><Icon className="w-5 h-5 text-white" /></div>
            </div>
        </motion.div>
    );
}

function DetailItem({ label, value, mono, children }: { label: string; value?: string | null; mono?: boolean; children?: React.ReactNode }) {
    return (
        <div className="p-3 bg-slate-50 rounded-xl">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
            {children ? children : (
                <p className={clsx('text-sm font-semibold text-slate-800 mt-0.5', mono && 'font-mono text-xs')}>{value || '—'}</p>
            )}
        </div>
    );
}